import { useEffect } from 'react';
import { httpService } from '../../services/httpService';
import { API_CONFIG } from '../../shared/constants';

interface IncompleteIntegration {
  trackingId: string;
  createdAt: string;
  status: 'ongoing' | 'completed';
  reminderSent?: boolean;
}

const STORAGE_KEY = 'incomplete_integration';
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes
const REMINDER_THRESHOLD_MINUTES = 15; // Send reminder after 15 minute
/**
 * Hook to monitor incomplete integrations and send reminder emails
 * Checks localStorage for incomplete integrations and sends a reminder
 * if the integration has been pending for more than 15 minutes
 */
export const useIncompleteIntegrationReminder = () => {
  useEffect(() => {
    const checkAndSendReminder = async () => {
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (!storedData) {
        return;
      }

      try {
        const data: IncompleteIntegration = JSON.parse(storedData);

        // Skip if already completed
        if (data.status === 'completed') {
          localStorage.removeItem(STORAGE_KEY);
          return;
        }

        // Skip if reminder was already sent for this integration
        if (data.reminderSent) {
          return;
        }

        const createdAt = new Date(data.createdAt);
        const now = new Date();
        const minutesElapsed = (now.getTime() - createdAt.getTime()) / (1000 * 60);


        // Send reminder if threshold minutes have passed
        if (minutesElapsed >= REMINDER_THRESHOLD_MINUTES) {
          await httpService.post(
            API_CONFIG.path.sendIncompleteIntegrationReminder,
            {
              tracking_id: data.trackingId,
              created_at: data.createdAt
            }
          );

          // Mark as sent instead of removing, so we don't send again after page refresh
          data.reminderSent = true;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        }
      } catch (error) {
        console.error('[Incomplete Integration] Error checking incomplete integration:', error);
      }
    };

    // Check immediately on mount
    checkAndSendReminder();

    // Then check periodically
    const interval = setInterval(checkAndSendReminder, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, []);
};

/**
 * Track a new incomplete integration in localStorage
 * Call this when a user creates a new integration
 */
export const trackIncompleteIntegration = (
  trackingId: string,
  createdAt: string
) => {
  // Check if there's existing data for this tracking ID
  const existingData = localStorage.getItem(STORAGE_KEY);
  let reminderSent = false;
  let originalCreatedAt = createdAt;

  if (existingData) {
    try {
      const existing: IncompleteIntegration = JSON.parse(existingData);
      // Preserve reminderSent flag and original createdAt if it's the same tracking ID
      if (existing.trackingId === trackingId) {
        reminderSent = existing.reminderSent || false;
        // Preserve the original createdAt to avoid resetting the timer when adding multiple sources
        originalCreatedAt = existing.createdAt;
      }
    } catch (error) {
      console.error('Error parsing existing integration data:', error);
    }
  }

  const data: IncompleteIntegration = {
    trackingId,
    createdAt: originalCreatedAt,
    status: 'ongoing',
    reminderSent
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

/**
 * Mark an integration as complete and remove from tracking
 * Call this when a user completes their integration
 */
export const markIntegrationComplete = () => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Clear incomplete integration tracking for a specific tracking ID
 * Call this when a user deletes an incomplete integration
 */
export const clearIncompleteIntegration = (trackingId: string) => {
  const storedData = localStorage.getItem(STORAGE_KEY);
  if (storedData) {
    try {
      const data: IncompleteIntegration = JSON.parse(storedData);
      if (data.trackingId === trackingId) {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error clearing incomplete integration:', error);
    }
  }
};
